import React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import { CircleNotchIcon } from '@phosphor-icons/react/dist/ssr/CircleNotch'
// TODO: see if we shouldn't replace cx by tailwind merge (simple concatenation VS merging as an overload)
// The first one doesn't allow to overload the same class with different values, the second one does
// The first one is less flexible but helps to avoid too many changes, and require to split variants in different classes
// The second one is more flexible but can't garantee to have a strict graphic consistency

// TODO: rework variants to make it more flexible, even by creating new variants classes
export const buttonVariants = cva(
  'inline-flex items-center gap-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-primary-500 text-foreground hover:bg-primary-500/90 shadow-sm',
        secondary:
          'bg-secondary hover:bg-secondary/80 hover:bg-border border border-border',
        error:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 text-white', // TO REMOVE or RECYCLE? Never called
        outline:
          'border border-input hover:bg-white hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        icon: 'rounded-md border border-gray-300 hover:shadow disabled:bg-gray-200',
        action:
          'border border-input bg-zinc-50 hover:bg-white hover:text-slate-700 text-slate-500',
        underline: 'underline',
      },
      border: {
        default: 'rounded-md',
        none: '',
      },
      justify: {
        default: 'justify-center',
        start: 'justify-start',
      },
      ring: {
        default:
          'ring-offset-background focus:ring-primary-500 focus:ring-offset-2 focus-visible:ring-1 focus-visible:ring-offset-2 focus-visible:outline-none',
      },
      size: {
        default: 'h-10 px-4 py-2 text-sm',
        sm: 'h-9 px-3 text-xs',
        lg: 'h-[50px] px-4 py-3 text-base',
        icon: 'h-10 px-3 py-2 text-sm',
        iconBig: 'h-12 px-3 py-4 text-base',
        tiny: 'px-2 py-1 text-xs',
        none: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      border: 'default',
      ring: 'default',
      justify: 'default',
    },
  },
)

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    isLoading?: {
      loading?: boolean
      loaderClassName?: string
    }
  }

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props: ButtonProps, ref) => {
    const {
      className,
      variant,
      size,
      border,
      ring,
      justify,
      asChild,
      isLoading,
      ...rest
    } = props
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        ref={ref}
        className={twMerge(
          buttonVariants({
            variant,
            size,
            border,
            ring,
            justify,
          }),
          className,
        )}
        {...rest}
      >
        {isLoading?.loading ? (
          <CircleNotchIcon
            className={twMerge(
              'animate-spin size-4',
              isLoading.loaderClassName,
            )}
          />
        ) : (
          props.children
        )}
      </Comp>
    )
  },
)

Button.displayName = 'Button'
